import { ChangeDetectionStrategy, Component, Injector, Input, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { FieldTree } from '@angular/forms/signals'

import { Lasku, LaskuBase } from '@lemon/core-firebase/model/lasku'

import { Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { DateService } from '@lemon/core/service/date.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FieldErrors, FormValidationService } from '../../../_jaettu-angular/service/form-validation.service'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'
import { LaskuLomake } from '../../../laskut/lasku-rivit.component'

@Component({
  selector: 'app-lasku-muut-tiedot',
  templateUrl: './lasku-muut-tiedot.component.html',
  styleUrls: ['./lasku-muut-tiedot.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuMuutTiedotComponent implements OnInit, OnDestroy {
  @Input() paivitaValidaatiot: any
  @Input() parent: FieldTree<LaskuLomake>
  @Input() laskuObservable: Observable<{ juurilasku: Lasku; kasiteltava: LaskuBase }>
  @Input() naytaViivastuskorko: boolean = false
  @Input() kateislasku = false
  @Input() naytaVainViitteenneJaLisatiedot = false

  readonly minErapaivaDate = signal(new Date())
  maksuaikaa = 14
  fieldErrors: FieldErrors = {}

  private _laskuVaihtuu = new Subject<void>()
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private validationService: FormValidationService,
    private laskuSharedService: LaskuSharedService,
    private timestampService: TimestampService,
    private dateService: DateService,
  ) {}

  ngOnInit() {
    this.laskuObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lasku => {
      this._laskuVaihtuu.next()
      this.setupForm(this.parent, lasku.juurilasku, lasku.kasiteltava)
    })
  }

  ngOnDestroy() {
    this._laskuVaihtuu.next()
    this._laskuVaihtuu.complete()
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private setupForm(parent: FieldTree<LaskuLomake>, juurilasku: Lasku, kasiteltava: LaskuBase) {
    // parent.removeControl('laskunpvm')
    // parent.removeControl('erapaiva')
    // parent.removeControl('maksuaikaa')
    // parent.removeControl('toimituspvm')
    // parent.removeControl('lisatiedot')
    // parent.removeControl('viitteenne')

    if (kasiteltava.pvm) {
      this.minErapaivaDate.set(this.timestampService.lisaaPaiviaTimestamp(kasiteltava.pvm, 1).toDate())
    } else {
      this.minErapaivaDate.set(this.timestampService.lisaaPaiviaTimestamp(this.timestampService.now(), 1).toDate())
    }

    if (kasiteltava.erapvm) {
      this.maksuaikaa = this.laskuSharedService.laskeMaksuaikaa(kasiteltava)
    }

    // Luo form controllit
    // parent.addControl('laskunpvm',        new FormControl({value: this.dateService.timestampToDate(kasiteltava.pvm), disabled: true}))
    // parent.addControl('erapaiva',         new FormControl(this.dateService.timestampToDate(kasiteltava.erapvm), [ Validators.required ]))
    // parent.addControl('maksuaikaa',       new FormControl(this.maksuaikaa, [ Validators.required, Validators.min(1) ]))
    // parent.addControl('toimituspvm',      new FormControl(this.dateService.timestampToDate(kasiteltava.toimituspvm)))
    // parent.addControl('lisatiedot',       new FormControl(kasiteltava.lisatiedot))
    // parent.addControl('viitteenne',       new FormControl(kasiteltava.viitteenne))

    asetaArvo(parent.laskunpvm, this.dateService.timestampToDate(kasiteltava.pvm))
    asetaArvo(parent.erapaiva, this.dateService.timestampToDate(kasiteltava.erapvm))
    asetaArvo(parent.maksuaikaa, this.maksuaikaa)
    asetaArvo(parent.toimituspvm, this.dateService.timestampToDate(kasiteltava.toimituspvm))
    asetaArvo(parent.lisatiedot, kasiteltava.lisatiedot ?? '')
    asetaArvo(parent.viitteenne, kasiteltava.viitteenne ?? '')
    asetaArvo(parent.viivastyskorko, juurilasku.viivastyskorkoprosentti ?? null)

    // Aseta kuuntelijat
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._laskuVaihtuu))
    muutokset(parent.laskunpvm).subscribe((value: Date) => {
      kasiteltava.pvm = this.timestampService.dateToTimestamp(value)
      kasiteltava.pvml = this.dateService.dateToLocalDate(value)
      kasiteltava.p = this.dateService.dateToNumber(value)
      if (this.kateislasku) {
        asetaArvo(parent.erapaiva, value)
      }
    })
    muutokset(parent.erapaiva).subscribe((value: Date) => {
      kasiteltava.erapvm = this.timestampService.dateToTimestamp(value)
      kasiteltava.erapvml = this.dateService.dateToLocalDate(value)
      kasiteltava.erap = this.dateService.dateToNumber(value)
      this.maksuaikaa = this.laskuSharedService.laskeMaksuaikaa(kasiteltava)
      asetaArvo(parent.maksuaikaa, this.maksuaikaa, { ilmoita: false })
    })
    muutokset(parent.maksuaikaa).subscribe(value => {
      this.maksuaikaa = value
      if (this.maksuaikaa && kasiteltava.pvm) {
        const timestamp = this.timestampService.lisaaPaiviaTimestamp(kasiteltava.pvm, this.maksuaikaa)
        const asLocalDate = this.dateService.timestampToLocalDate(timestamp)
        kasiteltava.erapvm = timestamp
        kasiteltava.erapvml = asLocalDate
        kasiteltava.erap = this.dateService.localDateToNumber(asLocalDate)
        const erapaiva: Date = timestamp ? timestamp.toDate() : null
        asetaArvo(parent.erapaiva, erapaiva, { ilmoita: false })
      }
    })
    muutokset(parent.toimituspvm).subscribe((value: Date) => {
      kasiteltava.toimituspvm = this.timestampService.dateToTimestamp(value)
      kasiteltava.toimituspvml = this.dateService.dateToLocalDate(value)
      kasiteltava.toimitusp = this.dateService.dateToNumber(value)
    })
    muutokset(parent.lisatiedot).subscribe(value => {
      // console.log(value, this.validationService.processValue(value))
      kasiteltava.lisatiedot = this.validationService.sanitizeWhitespace(value)
    })
    muutokset(parent.viitteenne).subscribe(value => {
      // console.log(value, this.validationService.processValue(value))
      kasiteltava.viitteenne = this.validationService.processValue(value)
    })

    muutokset(parent.viivastyskorko).subscribe(value => {
      // console.log(value, this.validationService.processValue(value))
      juurilasku.viivastyskorkoprosentti = this.validationService.processValue(value)
    })
  }

  get maksuaikaaField() {
    return this.parent.maksuaikaa
  }

  get viivastyskorkoField() {
    return this.parent.viivastyskorko
  }

  get erapaiva() {
    return this.parent.erapaiva
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }
}
